<script setup>
// FLOW-12 (G7, G14) — o JSON do teste no editor da casa (Monaco), com o serviço de JSON dele: o
// objeto sem `properties` na gaveta "Dados do teste", o "Editar como JSON" da gaveta e, na caixa do
// passo, a saída fixada. O JSON quebrado aparece na linha pelo Monaco; quem decide se ele serve é
// quem usa (`JSON.parse` no salvar), e a frase do erro fica embaixo, com o glifo e o texto neutro.
//
// Como o `CodigoJs.vue`: o tema sai dos tokens do OS e segue a classe `dark`, e o texto trocado por
// fora (o formulário ao lado, o "Usar estes dados") entra como uma edição própria do desfazer. O
// branco do fim não conta como diferença (`mesmoCodigo`).
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { aplicarTema, carregarJson, monaco } from './monaco';
import { mesmoCodigo } from './documentoYaml';

const props = defineProps({
  modelValue: { type: String, default: '' },
  somenteLeitura: { type: Boolean, default: false },
  // O id do campo (o `<label for>` e o foco do "Executar" com erro) e o nome que o leitor de tela diz.
  idDoCampo: { type: String, default: null },
  rotulo: { type: String, default: '' },
  // O valor tem um problema (o contorno rubi, como o do `Input`); a frase fica com quem usa.
  erro: { type: Boolean, default: false },
  // A altura do editor: `campo` (um param, ~8 linhas) ou `cheio` (ocupa o espaço de quem o põe).
  altura: { type: String, default: 'campo' },
});

const emit = defineEmits(['update:modelValue', 'sair']);

const { t } = useI18n();
const host = useTemplateRef('host');
const carregando = ref(true);

let editor = null;
let modelo = null;
let observador = null;
let desmontado = false;

const aplicarTemaAtual = () =>
  aplicarTema(host.value, !!host.value.closest('.dark'));

onMounted(async () => {
  await carregarJson();
  if (desmontado) return;
  modelo = monaco.editor.createModel(
    props.modelValue,
    'json',
    monaco.Uri.parse(
      `inmemory://powerflow/${Date.now()}-${Math.random().toString(36).slice(2)}.teste.json`
    )
  );
  carregando.value = false;
  aplicarTemaAtual();
  editor = monaco.editor.create(host.value, {
    model: modelo,
    automaticLayout: true,
    fixedOverflowWidgets: true,
    fontFamily: getComputedStyle(host.value).fontFamily,
    fontSize: 13,
    lineHeight: 20,
    lineNumbers: 'off',
    glyphMargin: false,
    folding: true,
    lineDecorationsWidth: 8,
    minimap: { enabled: false },
    padding: { top: 10, bottom: 10 },
    readOnly: props.somenteLeitura,
    scrollBeyondLastLine: false,
    renderLineHighlight: 'none',
    tabSize: 2,
    wordWrap: 'on',
    ariaLabel: props.rotulo || t('POWER_TOOLS.FLOW_EDITOR.TEST.DRAWER.JSON'),
  });
  modelo.onDidChangeContent(() => emit('update:modelValue', modelo.getValue()));
  editor.onDidBlurEditorText(() => emit('sair'));

  observador = new MutationObserver(aplicarTemaAtual);
  [document.documentElement, document.body].forEach(alvo =>
    observador.observe(alvo, { attributes: true, attributeFilter: ['class'] })
  );
});

onBeforeUnmount(() => {
  desmontado = true;
  observador?.disconnect();
  editor?.dispose();
  modelo?.dispose();
});

watch(
  () => props.modelValue,
  valor => {
    if (!modelo || mesmoCodigo(valor, modelo.getValue())) return;
    modelo.pushStackElement();
    modelo.pushEditOperations(
      [],
      [{ range: modelo.getFullModelRange(), text: valor }],
      () => null
    );
    modelo.pushStackElement();
  }
);
watch(
  () => props.somenteLeitura,
  valor => editor?.updateOptions({ readOnly: valor })
);

// O "Executar" com o campo errado põe o foco aqui (G13), e o "Editar" da saída fixada também.
function focar() {
  if (editor) editor.focus();
  else host.value?.focus();
}

defineExpose({ focar });
</script>

<template>
  <div
    class="relative overflow-hidden rounded-lg bg-n-alpha-black2 outline outline-1 -outline-offset-1 focus-within:outline-n-brand"
    :class="[
      erro
        ? 'outline-n-ruby-8 focus-within:outline-n-ruby-9'
        : 'outline-n-weak hover:outline-n-slate-6',
      altura === 'cheio' ? 'h-full min-h-[200px]' : 'h-40',
    ]"
    data-codigo-json
  >
    <div
      v-if="carregando"
      class="absolute inset-0 flex items-center justify-center gap-2 text-xs text-n-slate-11"
    >
      <Spinner class="size-4" />
      {{ t('POWER_TOOLS.FLOW_EDITOR.TEST.DRAWER.JSON_LOADING') }}
    </div>
    <div
      :id="idDoCampo"
      ref="host"
      tabindex="-1"
      class="w-full h-full font-mono outline-none"
      :aria-invalid="erro || undefined"
    />
  </div>
</template>
